import { Link } from '@reach/router';
import React from 'react';
import { Collection } from 'shopify-storefront-api-typings';
import styled, { css } from 'styled-components';
import { withQuery } from '../data/withQuery';
import { Metafields, Product } from '@/types/ecommerce.types';
import { getMetafieldV2 } from '@/utils/utils';
import { ProductOptionsTitle } from './ProductOptions.styles';
import { colors } from '@/themes/colorsMapping';
import { useGalleryCornerImages } from '@/hooks/useGalleryCornerImages';

const StyledLink = styled(Link)<{ $availableToSell?: boolean }>`
  ${(props) =>
    !props.$availableToSell &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}
  span {
    padding: 5px 10px;
    display: inline-block;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
  }
`;
StyledLink.displayName = 'ColorSwatch';

const SwatchesList = styled.ul`
  padding: 3px;
  margin-top: 13px;
  position: relative;
  display: grid;
  list-style-type: none;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 5px;

  @media (min-width: 800px) {
    margin-top: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }

  li {
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.darkGray};
    &.tmplt-product__connected-collection-item--current {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px var(--color-primary-20);
    }
  }
  a {
    background-color: transparent !important;
    background: none !important;
    display: flex;
    flex-direction: column;
    gap: 0;
    span {
      @media (max-width: 800px) {
        display: block;
        margin-top: -10px;
      }
    }
  }
  img {
    width: 100%;
    height: 70px;
    object-position: bottom;
    object-fit: cover;
  }
  .placeholder-img {
    height: 70px;
    width: 100%;
    background-color: #f1f0ec;
    background: linear-gradient(to right, #f1f0ec 0%, #ffffff 50%, #f1f0ec 100%);
    background-size: 800px 100%;
    animation: shimmer 1.5s infinite linear;
  }
  @keyframes shimmer {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: 800px 0;
    }
  }
`;

export type Color = {
  title: string;
  handle: string;
  color: string;
  colorName: string;
  availableToSell: boolean;
};

type PropTypes = {
  product: Product;
  colorCollection: Collection;
  className: string;
  pagePath?: string;
  showDefaultColor?: boolean;
  openStyle?: boolean;
  sectionTitle?: string;
  shopMetafields: Metafields;
};

type CornerImage = {
  colorCode?: string;
  image?: string;
};

type ProductItem = {
  availableForSale: boolean;
  productHandle: string;
  productTypeTitle: string;
  tileHandle: string;
  itemTilesArrClean: string[];
  currentProductTypeMatch: boolean;
};

type ProductCollectionGroupItemProps = {
  item: ProductItem;
  pagePath: string;
  currentVarCornerImages: CornerImage[] | null;
  currentVarColorCode?: string | null;
  fallbackImage?: string;
};

const ProductCollectionGroupItem = ({
  item,
  pagePath,
  currentVarCornerImages,
  currentVarColorCode,
  fallbackImage,
}: ProductCollectionGroupItemProps) => {
  const itemCornerImages = useGalleryCornerImages(item.itemTilesArrClean);
  let prodCornerImg: string | null = null;
  let currentProduct = false;

  if (item.currentProductTypeMatch) {
    currentVarCornerImages?.forEach((currentCornerItem) => {
      const currentColorCode = currentCornerItem?.colorCode;

      if (currentVarColorCode?.toLowerCase() === currentColorCode?.toLowerCase()) {
        prodCornerImg = currentCornerItem?.image || null;
      }
    });
    currentProduct = true;
  } else if (itemCornerImages && itemCornerImages.length > 0) {
    prodCornerImg = itemCornerImages[0]?.image || null;
  } else {
    prodCornerImg = fallbackImage || null;
  }

  return (
    <li
      className={`product-option-color-item ${
        currentProduct ? 'tmplt-product__connected-collection-item--current' : ''
      }`}
    >
      <StyledLink
        $availableToSell={item?.availableForSale}
        to={!currentProduct ? `${pagePath}/${item?.productHandle}` : ''}
        title={item?.productTypeTitle}
      >
        {prodCornerImg ? (
          <img src={prodCornerImg} alt="" />
        ) : (
          <div className="placeholder-img"></div>
        )}
        <span>{item?.productTypeTitle}</span>
      </StyledLink>
    </li>
  );
}; 

const ProductCollectionGroup = ({
  product,
  colorCollection,
  className,
  pagePath = '/products',
  sectionTitle,
}: PropTypes) => {
  const groupedProducts = product?.pdp_product_group?.reference;
  const currentVarColorCode = getMetafieldV2('color', product?.metafields);

  const tileHandles = React.useMemo(() => {
    const handles = (
      colorCollection?.products?.edges?.map((edge) => {
        const tileValue = getMetafieldV2('tile', (edge.node.metafields as any) || []);
        if (!tileValue) return null;
        try {
          const parsed = JSON.parse(tileValue);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed[0]?.split(':')?.[0];
          }
        } catch (e) {
          console.error('Error parsing tile value:', e);
        }
        return null;
      }) || []
    ).filter(Boolean) as string[];

    return Array.from(new Set(handles));
  }, [colorCollection]);

  const currentVarCornerImages = useGalleryCornerImages(tileHandles);

  const productItems: ProductItem[] =
    groupedProducts?.products?.nodes?.map((item: any) => {
      const itemTiles = item?.tile?.value;
      const itemTilesArr: string[] = JSON.parse(itemTiles);
      const itemTilesHandle = itemTilesArr[0]?.split(':')?.[0];

      const itemTilesArrClean = (
        itemTilesArr?.map((tile) => {
          const tileValue = tile;
          if (!tileValue) return null;
          try {
            const clean = tileValue?.split(':')?.[0];
            return clean;
          } catch (e) {
            console.error('Error parsing tile value:', e);
          }

          return null;
        }) || []
      ).filter(Boolean) as string[];

      const currentProductType = product?.product_type_display_name?.value;
      const itemProductType = item?.product_type_display_name?.value;

      return {
        availableForSale: item?.variants?.nodes[0]?.availableForSale,
        productHandle: item?.handle,
        productTypeTitle: item?.product_type_display_name?.value,
        tileHandle: itemTilesHandle,
        itemTilesArrClean: itemTilesArrClean,
        currentProductTypeMatch:
          currentProductType?.toLowerCase() === itemProductType?.toLowerCase(),
      };
    }) || [];

  if (!product || !groupedProducts) {
    return null;
  }

  return (
    <div className={className}>
      {sectionTitle && (
        <ProductOptionsTitle>
          {sectionTitle}: {'\u00A0'}
          <span>{product?.product_type_display_name?.value}</span>
        </ProductOptionsTitle>
      )}
      <SwatchesList>
        {productItems?.map((item, index) => {
          return (
            <ProductCollectionGroupItem
              key={item?.productHandle || index}
              item={item}
              pagePath={pagePath}
              currentVarCornerImages={currentVarCornerImages}
              currentVarColorCode={currentVarColorCode}
              fallbackImage={product?.images?.edges[0]?.node?.src || undefined}
            />
          );
        })}
      </SwatchesList>
    </div>
  );
};

ProductCollectionGroup.displayName = 'ProductCollectionGroup';
export default withQuery(ProductCollectionGroup);
